/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState } from 'react';
import {
    hooks as commonHooks,
    types as commonTypes,
    analytics as commonAnalytics,
} from '@sitebuilder/common';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';

import managerStyles, { thememanagerStyles } from './Manager.styles';

interface DynamicEvent {
    eventName: string;
    customEventName?: string;
    analyticItems: AnalyticsItem[];
}

interface AnalyticsItem {
    name: string;
    value: string;
}

interface ManagerProperties {
    link?: string;
    linkName?: string;
    managerName?: string;
    managerInfo?: string;
    isCompany?: boolean;
    isImgLink?: boolean;
    imageLink?: string;
    imgFileName?: string;
    imgFileType?: string;
    altText?: string;
    openInNewTab?: boolean;
    linkTarget?: string;
    iconName?: string;
    companyName?: string;
    companyDetails?: string;
    experienceBaseDate?: string;
    managerBio?: string;
    isBioReadMore?: boolean;
    investmentsDetails?: string;
    investmentsExperienceDetails?: boolean;
    readMoreText?: string;
    readLessText?: string;
    numberOfCharacters?: number;
    typographyClass?: string;
    isAccordion?: boolean;
    accordionIconType?: string;
    accordionIconOpenName?: string;
    accordionIconClosingName?: string;
    accordionIconOpenImage?: string;
    accordionIconClosingImage?: string;
    openFirstAccordion?: boolean;
    isAddPlaceholder?: boolean;
    widgetInstanceParentId?: string;
    useAnalytics?: boolean;
    dynamicEvents?: DynamicEvent[];
    useLowerCase?: boolean;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase, ManagerProperties {}

interface WidgetProps extends commonTypes.WidgetInstanceProps, ManagerProperties {}

const Manager = (props: WidgetProps) => {
    const { Placeholder } = commonHooks.useAppContext();
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const {
        styles,
        link,
        linkName,
        managerName,
        managerInfo,
        isCompany,
        isImgLink,
        imgFileName,
        openInNewTab,
        imgFileType,
        imageLink,
        altText,
        iconName,
        companyName,
        companyDetails,
        experienceBaseDate,
        linkTarget,
        managerBio,
        isBioReadMore,
        readMoreText,
        readLessText,
        numberOfCharacters,
        investmentsExperienceDetails,
        investmentsDetails,
        isAddPlaceholder,
        widgetInstanceParentId,
        useAnalytics,
        dynamicEvents,
        useLowerCase,
        ...rest
    } = widgetProperties || props;
    const data: any = props.data;
    const theme = data?.themeSelected || props?.theme;

    const widgetInstance = widgetInstanceParentId || '';

    const CHILDREN_PLACEHOLDER_ID = 'children';

    const i18n = commonHooks.useLocalization();
    const tracking = commonAnalytics.useTracking();

    const [showImage, setShowImage] = useState(true);
    // Added for accordion functionality
    const [accordionOpen, setAccordionOpen] = useState(rest?.openFirstAccordion || false);

    const managerNameHtml = i18n.format(managerName || '', data);
    const managerInfoHtml = i18n.format(managerInfo || '', data);
    const managerBioHtml = i18n.format(managerBio || '', data);
    const managerLinkHtml = i18n.format(link || '', data);
    const managerLinkNameHtml = i18n.format(linkName || '', data);
    const _readMoreText = i18n.format(readMoreText || '', data);
    const _readLessText = i18n.format(readLessText || '', data);
    const _investmentsDetails = i18n.format(investmentsDetails || '', data);

    const baseYear = new Date(experienceBaseDate || '1980-02-02').getFullYear();
    const currentYear = new Date().getFullYear();
    const expStartYear = new Date(
        data?.manager?.industryExperienceSince || '2001-01-01'
    ).getFullYear();
    const fundYear = new Date(data?.startDate || '2015-01-01').getFullYear();
    const experience = currentYear - expStartYear;
    const fundExperience = currentYear - fundYear;
    const experiencePercent = (experience / (currentYear - baseYear)) * 100;
    const fundPercent = (fundExperience / (fundYear - baseYear)) * 100;

    let target = linkTarget || '_self';

    if (openInNewTab) {
        target = '_blank';
    }

    const ReadMore = (text: any) => {
        const [isReadMore, setIsReadMore] = useState(true);
        const toggleReadMore = () => {
            tracking({
                event: 'Read Bio',
                data: data,
                dynamicEvents: dynamicEvents,
                useAnalytics: useAnalytics,
                managerNameHtml: managerNameHtml,
                useLowerCase: useLowerCase,
            });
            setIsReadMore(!isReadMore);
        };
        return (
            <p className="read-more-text">
                <span
                    className={'read-text'}
                    dangerouslySetInnerHTML={{
                        __html: isReadMore ? text.slice(0, numberOfCharacters) : text || '',
                    }}
                />
                {text && (
                    <span
                        className={'read-or-hide'}
                        onClick={toggleReadMore}
                        data-testid={'readMoreButton'}
                        dangerouslySetInnerHTML={{
                            __html: isReadMore ? _readMoreText : _readLessText || '',
                        }}
                    />
                )}
            </p>
        );
    };

    const ManagerTemplate = () => (
        <div
            className={`manager ${
                widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
            }`}
        >
            <img
                className="managerImg"
                src={
                    isImgLink
                        ? imageLink
                        : `/images/${imgFileName || 'manager-test'}.${imgFileType || 'jpg'}`
                }
                // if altText is not provided, it will be tagged as decorative image
                alt={altText}
            />
            <div className="manager-bio-name">
                <p
                    data-testid={'managerName'}
                    className="managerName"
                    dangerouslySetInnerHTML={{
                        __html: managerNameHtml || '',
                    }}
                />
                {managerInfoHtml?.length > 0 && (
                    <p
                        className="manager-info"
                        dangerouslySetInnerHTML={{ __html: managerInfoHtml }}
                    />
                )}
                {!isBioReadMore ? (
                    <p
                        className="manager-bio"
                        dangerouslySetInnerHTML={{
                            __html: managerBioHtml || '',
                        }}
                    />
                ) : (
                    <div className={'bio-container'}>{ReadMore(managerBioHtml)}</div>
                )}
            </div>
            <a href={managerLinkHtml} className="link" rel="noreferrer" target={target}>
                <span>
                    <i className={`${iconName}`} />
                </span>
                <span className="link-name">{managerLinkNameHtml}</span>
            </a>
            <div className="manager-experience">
                {data?.manager?.industryExperienceSince ? (
                    <div className={`experience-bar`}>
                        <div className={`label`}>{`${experience} ${
                            data?.resourceStrings?._managerYearsOfExp || `years of experience`
                        }`}</div>
                        <div className={`starter`}>
                            <div
                                className={`filler`}
                                style={{ width: `${100 - experiencePercent}%` }}
                            />
                        </div>
                    </div>
                ) : (
                    ''
                )}
                {investmentsExperienceDetails && (
                    <div className={'investment-experience'}>
                        {_investmentsDetails?.length > 0 && (
                            <span
                                className="investment-experience-info"
                                dangerouslySetInnerHTML={{ __html: _investmentsDetails }}
                            />
                        )}
                        <span>{fundYear}</span>
                    </div>
                )}
                <div className={`fund-bar`}>
                    <div className={`starter`} data-testid={'starter'}>
                        <div className={`filler`} style={{ width: `${100 - fundPercent}%` }} />
                    </div>
                    <div className={`label`}>
                        <p>{baseYear}</p>
                        <p>{`${fundExperience} ${
                            data?.resourceStrings?._managerYearsOnFund || `years on fund`
                        }`}</p>
                    </div>
                </div>
            </div>
        </div>
    );

    const CompanyTemplate = () => (
        <div className={`company ${showImage ? 'has-image' : 'without-image'}`}>
            {showImage && (
                <img
                    className="companyImg"
                    src={isImgLink ? imageLink : `/images/${imgFileName}.${imgFileType}`}
                    alt={''}
                    onError={() => setShowImage(false)}
                />
            )}
            <p className="companyDetails">
                <p className="companyName margin-bottom-zero">{companyName}</p>
                <p className="companyDesc margin-bottom-zero">{companyDetails}</p>
                {managerLinkNameHtml && (
                    <p>
                        <a
                            href={managerLinkHtml}
                            className="link"
                            rel="noreferrer"
                            target={target}
                            tabIndex={-1}
                        >
                            <button aria-label={managerLinkHtml}>
                                <span className="icon">
                                    <i className={`${iconName}`} />
                                </span>
                                <span className="link-name">{managerLinkNameHtml}</span>
                            </button>
                        </a>
                    </p>
                )}
            </p>
        </div>
    );

    return (
        <>
            {rest?.isAccordion &&
                (rest?.accordionIconType === 'FontAwesome Icon' ? (
                    <div className={'accordion-section'}>
                        <i
                            className={`accordion-icon fas ${
                                accordionOpen
                                    ? rest?.accordionIconOpenName
                                    : rest?.accordionIconClosingName
                            }`}
                            onClick={() => setAccordionOpen(!accordionOpen)}
                            onKeyDown={() => setAccordionOpen(!accordionOpen)}
                            tabIndex={0}
                        />
                        <span
                            data-testid={'accordionManagerName'}
                            className="accordionManagerName"
                            dangerouslySetInnerHTML={{
                                __html: managerNameHtml || '',
                            }}
                        />
                    </div>
                ) : rest?.accordionIconType === 'Image' ? (
                    <div className={'accordion-section'}>
                        <img
                            src={
                                accordionOpen
                                    ? rest?.accordionIconOpenImage
                                    : rest?.accordionIconClosingImage
                            }
                            className={`accordion-icon`}
                            alt={accordionOpen ? 'accordion-toggle-open' : 'accordion-closing-icon'}
                            onClick={() => setAccordionOpen(!accordionOpen)}
                            onKeyDown={() => setAccordionOpen(!accordionOpen)}
                            tabIndex={0}
                        />
                        <span
                            data-testid={'accordionManagerName'}
                            className="accordionManagerName"
                            dangerouslySetInnerHTML={{
                                __html: managerNameHtml || '',
                            }}
                        />
                    </div>
                ) : null)}
            {rest?.isAccordion && accordionOpen && (
                <div
                    className={`manager-widget`}
                    data-testid={'manager-widget'}
                    css={css`
                        ${!theme ? managerStyles : thememanagerStyles}${getTextSchemaProperties(
                            widgetProperties,
                            data?.colorSpace
                        )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                            widgetProperties
                        )}${styles || ''}
                    `}
                >
                    {isCompany ? <CompanyTemplate /> : <ManagerTemplate />}
                    {isAddPlaceholder && (
                        <Placeholder
                            contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}-bottom`}
                            parentWidgetInstanceId={widgetInstance}
                            data={data}
                        />
                    )}
                </div>
            )}
            {!rest?.isAccordion && (
                <div
                    className={`manager-widget`}
                    data-testid={'manager-widget'}
                    css={css`
                        ${!theme ? managerStyles : thememanagerStyles}${getTextSchemaProperties(
                            widgetProperties,
                            data?.colorSpace
                        )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                            widgetProperties
                        )}${styles || ''}
                    `}
                >
                    {isCompany ? <CompanyTemplate /> : <ManagerTemplate />}
                    {isAddPlaceholder && (
                        <Placeholder
                            contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}-bottom`}
                            parentWidgetInstanceId={widgetInstance}
                            data={data}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default Manager;
